import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import type { ConsultingBrokerStewardshipResponse } from "./types";

export type StewardshipDateBasis = "submitted" | "effective";

export type BrokerStewardshipQueryParams = {
  startDate?: string;
  endDate?: string;
  dateBasis?: StewardshipDateBasis;
  enabled?: boolean;
};

export const consultingStewardshipSummaryQueryKey = (
  startDate?: string,
  endDate?: string,
) =>
  [
    "consulting",
    "stewardshipSummary",
    startDate ?? null,
    endDate ?? null,
  ] as const;

export const consultingStewardshipQueryKey = (
  brokerId?: string,
  startDate?: string,
  endDate?: string,
  dateBasis?: StewardshipDateBasis,
) =>
  [
    "consulting",
    "stewardship",
    brokerId ?? null,
    startDate ?? null,
    endDate ?? null,
    dateBasis ?? "submitted",
  ] as const;

type BrokerStewardshipQueryKey = ReturnType<
  typeof consultingStewardshipQueryKey
>;

export function useConsultingStewardshipSummaryQuery(
  options: BrokerStewardshipQueryParams = {},
): UseQueryResult<ConsultingBrokerStewardshipResponse> {
  const axiosInstance = useApi();
  const { startDate, endDate, enabled = true } = options;

  return useQuery<ConsultingBrokerStewardshipResponse>({
    queryKey: consultingStewardshipSummaryQueryKey(startDate, endDate),
    queryFn: () =>
      axiosInstance
        .get<ConsultingBrokerStewardshipResponse>(
          "/consulting/v1/stewardship/summary",
          {
            params: {
              ...(startDate ? { start_date: startDate } : {}),
              ...(endDate ? { end_date: endDate } : {}),
            },
          },
        )
        .then((response) => response.data),
    enabled,
  });
}

export function useBrokerStewardshipQuery(
  brokerId?: string,
  options: BrokerStewardshipQueryParams = {},
): UseQueryResult<ConsultingBrokerStewardshipResponse> {
  const axiosInstance = useApi();
  const { startDate, endDate, dateBasis, enabled = true } = options;

  return useQuery<ConsultingBrokerStewardshipResponse>({
    queryKey: consultingStewardshipQueryKey(
      brokerId,
      startDate,
      endDate,
      dateBasis,
    ),
    queryFn: () =>
      axiosInstance
        .get<ConsultingBrokerStewardshipResponse>(
          `/consulting/v1/brokers/${brokerId}/stewardship`,
          {
            params: {
              ...(startDate ? { start_date: startDate } : {}),
              ...(endDate ? { end_date: endDate } : {}),
              // Omit for the submitted default so existing URLs/caches are unchanged.
              ...(dateBasis === "effective" ? { date_basis: "effective" } : {}),
            },
          },
        )
        .then((response) => response.data),
    enabled: Boolean(brokerId) && enabled,
    placeholderData: (previousData, previousQuery) => {
      const previousBrokerId = (
        previousQuery?.queryKey as BrokerStewardshipQueryKey | undefined
      )?.[2];

      return previousBrokerId === (brokerId ?? null) ? previousData : undefined;
    },
  });
}
